import { CloseIcon, SignInPrompt, WordsHappenTextField } from '@words-happen/ui-components';
import type React from 'react';
import { useRef } from 'react';
import type { AccountOption, AccountOptionSearchResult } from '../account-options';
import styles from './account-search.module.scss';
import { accountTabLabel } from './account-tabs';

export interface AccountSearchProps {
  query: string;
  onQueryChange: (query: string) => void;
  /** The matches for the query, or undefined while there's no query and the tabs show instead. */
  result?: AccountOptionSearchResult;
  onChoose: (option: AccountOption) => void;
  /** A guest's sign-in, for a match that needs an account. */
  onSignIn?: () => void;
}

const resultCount = (count: number) => {
  if (count === 0) return 'No results';
  return count === 1 ? '1 result' : `${count} results`;
};

/** What the status region reads out: the count, and the sign-in suggestion when there is one. */
const announcement = (result: AccountOptionSearchResult | undefined, signInShown: boolean) => {
  if (!result) return '';
  const count = resultCount(result.options.length);
  return signInShown ? `${count}. Sign in to ${result.signInTo}` : count;
};

interface ResultsProps {
  query: string;
  result: AccountOptionSearchResult;
  onChoose: (option: AccountOption) => void;
}

const Results: React.FC<ResultsProps> = ({ query, result, onChoose }) => {
  if (result.options.length > 0) {
    return (
      <ul className={styles.results} aria-label="Search results">
        {result.options.map((option) => (
          <li key={option.id}>
            <button type="button" className={styles.result} onClick={() => onChoose(option)}>
              <span className={styles.label}>{option.label}</span>
              <span className={styles.where}>
                {accountTabLabel(option.tab)} · {option.section}
              </span>
            </button>
          </li>
        ))}
      </ul>
    );
  }
  // A guest whose only matches need an account gets the sign-in link instead of the empty state.
  if (result.signInTo) return null;
  return (
    <div className={styles.empty}>
      <p className={styles.emptyTitle}>Nothing matches “{query.trim()}”</p>
      <p className={styles.emptyHint}>Try a word like theme, keyboard or name.</p>
    </div>
  );
};

/**
 * The account screen's search: a field that finds options across both tabs, and the matches as a flat list. Escape or
 * the clear button empties it. The result count is announced as the player types.
 */
export const AccountSearch: React.FC<AccountSearchProps> = ({ query, onQueryChange, result, onChoose, onSignIn }) => {
  const input = useRef<HTMLInputElement>(null);
  const signInShown = Boolean(result?.signInTo && onSignIn);

  const clear = () => {
    onQueryChange('');
    input.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Escape' || query === '') return;
    event.preventDefault();
    clear();
  };

  return (
    <search className={styles.search} onKeyDown={handleKeyDown}>
      <WordsHappenTextField
        ref={input}
        className={styles.field}
        type="search"
        label="Search settings and profile"
        placeholder="Theme, keyboard, sign out…"
        autoComplete="off"
        enterKeyHint="search"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        rightSlot={
          query ? (
            <button type="button" className={styles.clear} aria-label="Clear search" onClick={clear}>
              <CloseIcon size={18} aria-hidden="true" />
            </button>
          ) : null
        }
      />
      <p className={styles.status} role="status">
        {announcement(result, signInShown)}
      </p>
      {result ? <Results query={query} result={result} onChoose={onChoose} /> : null}
      {signInShown && onSignIn ? (
        <SignInPrompt
          className={styles.signIn}
          prefix={null}
          action={`Sign in to ${result?.signInTo}`}
          onAction={onSignIn}
        />
      ) : null}
    </search>
  );
};
